import React, { useEffect, useRef, useState } from 'react';
import { omit } from 'lodash';
import { styled } from '@mui/material';

import { useSnackbar } from 'contexts/snackbar';
import { Box, Button, RichTextArea, Stack, Text } from 'shell/components';
import { stripHTML } from 'utils/helper';

const url = process.env.REACT_APP_OPEN_AI_API;

const makeRequest = async (messages) => {
    const token = localStorage.getItem('auth_token');
    if (!token) return;
    return await fetch(url, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            model: 'gpt-4',
            messages,
            stream: true,
        }),
    });
};

const OpenaiDemo = () => {
    const ref = useRef(null);
    const { message } = useSnackbar();

    const [isLoading, setIsLoading] = useState(false);
    const [conversation, setConversation] = useState([]);
    const [value, setValue] = useState('');

    const scrollToBottom = () => {
        if (ref.current) ref.current.scrollIntoView(false);
    };

    const handleResponse = async (response) => {
        const reader = response.body.getReader();
        while (true) {
            const { done, value } = await reader.read();
            if (done) {
                setIsLoading(false);
                scrollToBottom();
                break;
            }
            const dataStream = String.fromCharCode.apply(null, new Uint8Array(value));
            const splitDataStream = dataStream.split(',,').filter((chunk) => chunk && chunk.length);
            const chunks = splitDataStream.map((chunk) => {
                return JSON.parse(chunk);
            });
            chunks.forEach((chunk) => {
                const { id, choices } = chunk;
                if (choices[0].finish_reason !== 'stop') {
                    const { delta } = choices[0];
                    setConversation((current) => {
                        const [last] = current.slice(-1);
                        if (last.id === id) {
                            return [...current.slice(0, -1), { ...last, content: `${last.content}${delta.content}` }];
                        } else {
                            return [
                                ...current,
                                {
                                    id,
                                    ...delta,
                                },
                            ];
                        }
                    });
                    scrollToBottom();
                }
            });
        }
    };

    const handleClick = async () => {
        setIsLoading(true);
        setConversation((current) => {
            const newState = [
                ...current,
                {
                    role: 'user',
                    content: value,
                },
            ];
            makeRequest(newState.map((message) => omit({ ...message, content: stripHTML(message.content) }, ['id'])))
                .then((response) => handleResponse(response))
                .catch((e) => {
                    message.error('Something went wrong. Please check the console.');
                    console.log(e);
                });
            return newState;
        });
        setValue('');
    };

    useEffect(() => {
        scrollToBottom();
    }, [conversation.length]);

    return (
        <Stack sx={{ width: '100%', height: '100%' }} spacing={2}>
            <Text variant={'sh1'}>GPT-4 Sandbox</Text>
            <ParentStack direction={'row'} spacing={2}>
                <Stack alignItems={'flex-end'} spacing={2}>
                    <CustomRichTextArea disabled={isLoading} value={value} onChange={setValue} />
                    <Button loading={isLoading} onClick={handleClick}>
                        Send
                    </Button>
                </Stack>
                <ConversationStack spacing={2}>
                    {conversation.map((message, index) => (
                        <MessageStack key={index} $message={message} spacing={1}>
                            <Text color={'light'} variant={'p3'}>
                                {message.role === 'user' ? 'You' : 'GPT-4'}
                            </Text>
                            <Text rich>{message.content}</Text>
                        </MessageStack>
                    ))}
                    <Box ref={ref} />
                </ConversationStack>
            </ParentStack>
        </Stack>
    );
};

const ParentStack = styled(Stack)(() => {
    return {
        height: 'calc(100% - 24px - 8px)',
        '& > *': {
            flex: 1,
            width: '50%',
            '&:first-child': {
                height: '100%',
            },
        },
    };
});

const CustomRichTextArea = styled(RichTextArea)(() => {
    return {
        width: '100%',
        height: 'calc(100% - 8px - 36px)',
        '& > .MuiRichTextArea-root': {
            minHeight: '100%',
            maxHeight: '100%',
        },
    };
});

const ConversationStack = styled(Stack)(({ theme }) => {
    return {
        borderRadius: '4px',
        padding: theme.spacing(2),
        background: theme.palette.ensightful.neutral[100],
        overflowY: 'auto',
    };
});

const MessageStack = styled(Stack)(({ $message, theme }) => {
    return {
        borderRadius: '4px',
        width: '100%',
        padding: theme.spacing(2),
        ...($message.role === 'user'
            ? {
                  alignSelf: 'flex-end',
                  background: theme.palette.ensightful.neutral[50],
              }
            : {
                  background: theme.palette.ensightful.primary[100],
              }),
    };
});

export default OpenaiDemo;
